import React from 'react';
import { Tabs, Divider } from 'antd';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { OrderTroubleshootingToolComponent } from './OrderTroubleshootingTool';
import { SalesHierarchyTroubleshootingToolComponent } from './SalesHierarchyTroubleshootingTool';
import { GrowerContactsTroubleShootingToolComponent } from './GrowerContactsTroubleShootingTool';
import { GrowerAssociationTroubleshootingToolComponent } from './GrowerAssociationTroubleshootingTool';
import { GamePlansTroubleshootingToolComponent } from './GamePlansTroubleShootingTool';
import { ProductAndPricingTroubleshootingToolComponent } from './ProductAndPricingTroubleshootingTool';
import { GrowerHierarchyTroubleshootingToolComponent } from './GrowerHierarchyTroubleshootingTool';
import { SoilDataTroubleShootingToolComponent } from './SoilDataTroubleShootingToolComponent';
import { DataTransferTroubleshootingToolComponent } from './DataTransfer/DataTransferTroubleshootingTool';
import { UserManagementTool } from './UserManagement/UserManagementTool';
import ClearCacheTool from './ClearCacheTool';
import { AccountMigrationTool } from './AccountMigration/AccountMigrationTool';
import { GrowerJdConnectInfoTroubleShootingToolComponent } from './GrowerJdConnectInfo';

const TabContainer = styled.div`
	flex-grow: 1;
	flex-basis: 0;
	overflow: hidden;

	.ant-tabs {
		height: 100%;
	}

	.ant-tabs-content-holder {
		display: flex;
		position: relative;
	}

	.ant-tabs-nav {
		padding-top: 20px;
		font-weight: ${themeGet('fontWeights.bold')};
	}

	.ant-tabs-ink-bar {
		background-color: ${themeGet('colors.secondary')};
	}

	.ant-tabs-tab-active > .ant-tabs-tab-btn {
		color: ${themeGet('colors.white')};
	}

	.ant-tabs-tab:hover {
		color: ${themeGet('colors.primary')};
	}

	.ant-tabs-tab-active
	{
		background-color: ${themeGet('colors.secondary')};
	}

	.ant-tabs-left > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane, .ant-tabs-left > div > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
    	padding: 24px 20px;
	}
}
`;

interface ITroubleShootingToolsProps
{

}

export const TroubleShootingTools = (props: ITroubleShootingToolsProps) =>
{
	const theme = useTheme();

	const [activeTabKey, setActiveTabKey] = React.useState<string>('Sales Hierarchy');

	const tabItems : { label: string, key: string, children: React.ReactNode, style: React.CSSProperties }[] =
	[
		{
			label: 'Sales Hierarchy',
			key: 'Sales Hierarchy',
			children: <SalesHierarchyTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Grower Hierarchy',
			key: 'Grower Hierarchy',
			children: <GrowerHierarchyTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Grower Association',
			key: 'Grower Association',
			children: <GrowerAssociationTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Grower Contacts',
			key: 'Grower Contacts',
			children: <GrowerContactsTroubleShootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Grower JdConnect Info',
			key: 'Grower JdConnect Info',
			children: <GrowerJdConnectInfoTroubleShootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white}
		},
		{
			label: 'Products and Pricing',
			key: 'Products and Pricing',
			children: <ProductAndPricingTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Proposals and Orders',
			key: 'Proposals and Orders',
			children: <OrderTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Plans',
			key: 'Plans',
			children: <GamePlansTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'NRCS Soil Data',
			key: 'NRCS Soil Data',
			children: <SoilDataTroubleShootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white},
		},
		{
			label: 'Data Transfer',
			key: 'Data Transfer',
			children: <DataTransferTroubleshootingToolComponent />,
			style: {height: '100%', backgroundColor: theme.colors.white, minHeight: 460, minWidth:1024, flexGrow:1, overflow: 'auto', display: 'flex', alignItems: 'stretch'}
		},
		{
			label: 'User Management',
			key: 'User Management',
			children: <UserManagementTool />,
			style: {height: '100%', backgroundColor: theme.colors.white, minHeight: 460, minWidth:1024, width:'100%', flexGrow:1, overflow: 'auto', display: 'flex', alignItems: 'flex-start'}
		},
		{
			label: 'Clear Cache',
			key: 'Clear Cache',
			children: <ClearCacheTool />,
			style: {height: '100%', backgroundColor: theme.colors.white, minHeight: 460, minWidth:1024, width:'100%', flexGrow:1, overflow: 'auto', display: 'flex', alignItems: 'flex-start'}
		},
		{
			label: 'Account Migration',
			key: 'Account Migration',
			children: <AccountMigrationTool />,
			style: {height: '100%', backgroundColor: theme.colors.white, minHeight: 460, minWidth:1024, width:'100%', flexGrow:1, overflow: 'auto', display: 'flex', alignItems: 'flex-start'}
		},
	];

	// Make sure the active tab is actually the tab the user selected
	const onChange = (key: string) =>
	{
		setActiveTabKey(key);
	};

	return (
		<div
			className='TroubleShootingTools_MainContainer'
			style={{ height: '100%', overflow:'hidden', display: 'flex', flexDirection: 'column', backgroundColor: theme.colors.backgroundLM }}
		>
			<div className='TroubleShootingTools_TitleContainer' style={{ fontWeight: theme.fontWeights.bold, width: '100%', paddingLeft: 20, paddingTop: 20 }}>
				<span
					className='TroubleShootingTools_Title'
					style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.large }}
				>
					Troubleshooting Tools
				</span>
			</div>
			<Divider className='TroubleShootingTools_Divider' style={{ marginTop: 10, marginBottom: 0 }} />
			<TabContainer className='TroubleShootingTools_TabContainer' >
				<Tabs
					defaultActiveKey='0'
					tabPosition='left'
					className='TroubleShootingTools_Tabs'
					items={tabItems}
					onChange={onChange}
					activeKey={activeTabKey}
					destroyInactiveTabPane
				/>
			</TabContainer>
		</div>
	);
};

